import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { portalApi } from "@services/api/portalApi";
import { usePortalModeStore } from "@stores/usePortalModeStore";
import { useStaffOverrideStore } from "@stores/useStaffOverrideStore";
import { getAccountForPortal, msalInstance } from "@config/auth/msalConfig";
import { useMsal } from "@azure/msal-react";
import type {
	GlobalTimetableSettings,
	MyTimetableSettings,
	TimetableProfile,
	TimetableSettings,
	TimetableVisibility,
} from "./settings";

export function timetableRequestIdentity() {
	const { hostMode, viewMode } = usePortalModeStore.getState();
	return [
		hostMode,
		viewMode,
		useStaffOverrideStore.getState().override ?? "",
		getAccountForPortal(msalInstance.getAllAccounts(), hostMode)
			?.homeAccountId ?? "",
	];
}

export function useTimetableRequestIdentity() {
	const hostMode = usePortalModeStore((state) => state.hostMode);
	const viewMode = usePortalModeStore((state) => state.viewMode);
	const override = useStaffOverrideStore((state) => state.override);
	const { accounts } = useMsal();
	return [
		hostMode,
		viewMode,
		override ?? "",
		getAccountForPortal(accounts, hostMode)?.homeAccountId ?? "",
	];
}

export function useTimetableSettings() {
	const identity = useTimetableRequestIdentity();
	return useQuery({
		queryKey: ["timetable-settings", "mine", ...identity],
		staleTime: 24 * 60 * 60_000,
		gcTime: 24 * 60 * 60_000,
		queryFn: async () =>
			(await portalApi.get<MyTimetableSettings>("/timetable-settings/mine"))
				.data,
	});
}

export function useGlobalTimetableSettings(enabled: boolean) {
	const identity = useTimetableRequestIdentity();
	return useQuery({
		queryKey: ["timetable-settings", "global", ...identity],
		staleTime: 24 * 60 * 60_000,
		gcTime: 24 * 60 * 60_000,
		queryFn: async () =>
			(
				await portalApi.get<GlobalTimetableSettings>(
					"/timetable-settings/global",
				)
			).data,
		enabled,
	});
}

export function useSaveTimetableSettings() {
	const queryClient = useQueryClient();
	return useMutation({
		// Keep automatic writes in order, including across editor remounts.
		scope: { id: "timetable-settings" },
		mutationFn: async (
			request:
				| {
						kind: "mine" | "global";
						profile: TimetableProfile;
						settings: Partial<TimetableSettings>;
				  }
				| { kind: "reset"; profile: TimetableProfile }
				| { kind: "visibility"; visibility: TimetableVisibility },
		) => {
			if (request.kind === "reset")
				return portalApi.delete(`/timetable-settings/mine/${request.profile}`);
			if (request.kind === "visibility")
				return portalApi.put(
					"/timetable-settings/visibility",
					request.visibility,
				);
			return portalApi.put(
				`/timetable-settings/${request.kind}/${request.profile}`,
				request.settings,
			);
		},
		onSuccess: async () => {
			// Cancel in-flight payloads before changing publication, then clear and
			// refetch active timetables under the new server-side visibility rules.
			await queryClient.cancelQueries({ queryKey: ["timetable"] });
			await queryClient.cancelQueries({ queryKey: ["studentExamTimetable"] });
			await queryClient.cancelQueries({ queryKey: ["studentCurrentLesson"] });
			await queryClient.cancelQueries({ queryKey: ["studentLessonsToday"] });
			await queryClient.cancelQueries({ queryKey: ["extendedPersona"] });
			await queryClient.invalidateQueries({ queryKey: ["timetable-settings"] });
			await Promise.all([
				queryClient.resetQueries({ queryKey: ["timetable"] }),
				queryClient.resetQueries({ queryKey: ["studentExamTimetable"] }),
				queryClient.resetQueries({ queryKey: ["studentCurrentLesson"] }),
				queryClient.resetQueries({ queryKey: ["studentLessonsToday"] }),
				queryClient.resetQueries({ queryKey: ["extendedPersona"] }),
			]);
		},
	});
}
